"use client";

import { OnboardingItemLayout } from "@/components/Onboarding/components/OnboardingItemLayout";
import { Button } from "@/components/ui/button";
import {
  Action,
  ControlType,
  useOnboardingAnalytics,
} from "@/context/AnalyticsProvider";
import { useOnboardingStep } from "@/context/OnboardingStepProvider/OnboardingStepProvider";
import {
  ArrowRight,
  DollarSign,
  Gamepad2,
  type LucideIcon,
  Shield,
} from "lucide-react";
import Image from "next/image";
import { useEffect, useRef } from "react";
import { OnboardingStep, type OnboardingStepProps } from "../constants";

const FEATURE_CARDS = [
  {
    title: "DISCOVER\nGAMES",
    icon: Gamepad2,
  },
  {
    title: "DO\nQUESTS",
    icon: Shield,
  },
  {
    title: "EARN\nREWARDS",
    icon: DollarSign,
  },
];

const FeatureCard: React.FC<{ title: string; icon: LucideIcon }> = ({
  title,
  icon: Icon,
}) => {
  return (
    <div className="relative flex-1 aspect-[7/8]">
      {/* Background image */}
      <Image
        src="/images/onboarding/feature-card-background.webp"
        alt=""
        fill
        className="object-cover opacity-80"
      />

      {/* Content */}
      <div className="absolute inset-1 flex flex-col items-center justify-center gap-2 p-3 rounded-2xl border-[3px] border-white/10">
        <p className="text-center font-bold uppercase text-xs leading-tight tracking-wider whitespace-pre-line">
          {title}
        </p>
        <Icon className="text-white flex-shrink-0 text-2xl" />
      </div>
    </div>
  );
};

export const WelcomeInfo = ({ visible }: OnboardingStepProps) => {
  const { goToNextStep } = useOnboardingStep();
  const { track } = useOnboardingAnalytics();
  const { currentStep } = useOnboardingStep();
  const hasViewedRef = useRef(false);

  useEffect(() => {
    if (hasViewedRef.current || !visible) return;
    hasViewedRef.current = true;

    track({
      action: Action.VIEWED,
      extras: {
        id: OnboardingStep.WELCOME_INFO,
        step: currentStep,
      },
    });
  }, [track, visible, currentStep]);

  const handleButtonClick = () => {
    track({
      action: Action.PRESSED,
      extras: {
        id: OnboardingStep.WELCOME_INFO,
        step: currentStep,
        control: "next-step",
        controlType: ControlType.BUTTON,
      },
    });

    goToNextStep();
  };

  return (
    <OnboardingItemLayout className="justify-center text-center gap-8">
      {/* Top Background Image */}
      <div className="absolute top-0 left-0 right-0 w-full">
        <Image
          src="/images/onboarding/quest-difficulty-illus-top.webp"
          alt="Game characters top"
          width={1640}
          height={1022}
          className="object-cover object-top"
        />
      </div>

      {/* Bottom Background Image */}
      <div className="absolute bottom-0 left-0 right-0 w-full">
        <Image
          src="/images/onboarding/quest-difficulty-illus-bottom.webp"
          alt="Game characters bottom"
          width={1640}
          height={781}
          className="object-cover object-bottom"
        />
      </div>

      {/* Logo */}
      <Image
        src="/images/immutable-play-logo-horizontal.svg"
        alt="Immutable Play"
        width={200}
        height={40}
        className="relative object-contain"
      />

      {/* Title */}
      <h1 className="font-bold text-heading-lg leading-[1.1] px-4 max-w-md">
        Get rewarded for playing games!
      </h1>

      {/* Feature Cards */}
      <div className="flex flex-row gap-4 w-full justify-center px-4 max-w-lg">
        {FEATURE_CARDS.map((card) => (
          <FeatureCard key={card.title} title={card.title} icon={card.icon} />
        ))}
      </div>

      {/* Get Started Button */}
      <Button
        size="lg"
        className="w-full text-base font-semibold relative py-4"
        onClick={handleButtonClick}
        autoFocus
      >
        Get Started
        <ArrowRight className="absolute flex-shrink-0 right-6 text-xl" />
      </Button>
    </OnboardingItemLayout>
  );
};
